import { ensureAn26DesignStyles } from "./injectStyles.js";
import type { HoldingsPayload, HoldingsRow } from "../normalize.js";
import type { ChartLayout } from "../types.js";
import { formatRichTextHtml } from "./richText.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}


const SORT_KEYS: (keyof HoldingsRow)[] = [
  "ticker",
  "cusip",
  "name",
  "sector",
  "weight",
  "marketCap",
];

/** Collapse mega/micro (and * Cap variants) into large / mid / small for display and sort. */
function holdingsMarketCapLabel(raw: string): string {
  const t = raw.trim();
  if (!t || t === "-") return t;
  const bucket = t.replace(/\s+cap\s*$/i, "").trim();
  const lower = bucket.toLowerCase();
  if (lower === "mega") return "Mega";
  if (lower === "large") return "Large";
  if (lower === "mid") return "Mid";
  if (lower === "micro" || lower === "small") return "Small";
  return t;
}

const MARKET_CAP_ORDER: Record<string, number> = {
  Large: 0,
  Mid: 1,
  Small: 2,
  "-": 99,
};

function compareHoldings(a: HoldingsRow, b: HoldingsRow, key: keyof HoldingsRow, dir: 1 | -1): number {
  let cmp = 0;
  switch (key) {
    case "weight":
      cmp = a[key] - b[key];
      break;
    
    case "marketCap": {
      const la = holdingsMarketCapLabel(a.marketCap);
      const lb = holdingsMarketCapLabel(b.marketCap);
      const oa = MARKET_CAP_ORDER[la] ?? 50;
      const ob = MARKET_CAP_ORDER[lb] ?? 50;
      cmp = oa !== ob ? oa - ob : la.localeCompare(lb, undefined, { sensitivity: "base" });
      break;
    }
    default:
      cmp = String(a[key]).localeCompare(String(b[key]), undefined, { sensitivity: "base" });
  }
  return dir * cmp;
}

function rowHtml(r: HoldingsRow): string {
  return `<tr>
    <td>${esc(r.ticker)}</td>
    <td>${esc(r.cusip)}</td>
    <td>${esc(r.name)}</td>
    <td>${esc(r.sector)}</td>
    <td>${esc(`${r.weight.toFixed(2)}%`)}</td>
    <td>${esc(holdingsMarketCapLabel(r.marketCap))}</td>
  </tr>`;
}

const TH_LABELS: Record<keyof HoldingsRow, string> = {
  ticker: "Ticker",
  cusip: "CUSIP",
  name: "Name",
  sector: "Sector",
  weight: "Target Weight",
  marketCap: "Market Cap",
};

const PREVIEW_ROW_COUNT = 5;

export interface HoldingsRenderOptions {
  title?: string | null;
  subtitle?: string | null;
  disclaimer?: string | null;
  pdfUrl?: string | null;
  layout?: ChartLayout;
}

export function mountHoldings(
  container: HTMLElement,
  data: HoldingsPayload,
  opts?: HoldingsRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Holdings";
  const subtitle =
    opts?.subtitle ?? data.subtitle ?? (data.asOf ? `As of ${data.asOf}` : "");
  const disclaimer =
    opts?.disclaimer === null ? "" : (opts?.disclaimer ?? data.disclaimer ?? "");
  const pdfUrl = opts?.pdfUrl ?? null;
  const tableId = `${chartlessId(title)}-holdings-table`;
  const base = [...data.rows];
  const previewRows = base.slice(0, PREVIEW_ROW_COUNT);
  const hiddenRowsCount = Math.max(0, base.length - PREVIEW_ROW_COUNT);
  let sortKey: keyof HoldingsRow | null = null;
  let sortDir: "asc" | "desc" = "asc";
  let expanded = false;

  const sortedRows = (rows: HoldingsRow[]): HoldingsRow[] => {
    const key = sortKey;
    if (!key) return [...rows];
    const d = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => compareHoldings(a, b, key, d));
  };

  const displayedRows = (): HoldingsRow[] => {
    if (expanded) return sortedRows(base);
    return sortedRows(previewRows);
  };

  const theadCells = SORT_KEYS.map(
    (k) =>
      `<th scope="col" class="an26-holdings__th" data-sort="${k}" tabindex="0" aria-sort="none">${esc(TH_LABELS[k])}</th>`
  ).join("");

  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h3 class="an26-root__title">${esc(String(title))}</h3>
        ${subtitle ? `<p class="an26-root__subtitle">${esc(subtitle)}</p>` : ""}
      </header>`;
  const rootClass = embedded ? "an26-holdings an26-root an26-root--embedded" : "an26-holdings an26-root";

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}

      <div class="an26-holdings__scroll">
        <table id="${tableId}" class="an26-table an26-holdings__table" role="grid">
          <thead>
            <tr>${theadCells}</tr>
          </thead>
          <tbody class="js-an26-holdings-body"></tbody>
        </table>
      </div>
      ${
        hiddenRowsCount
          ? `<div class="an26-holdings__more-wrap js-an26-holdings-more-wrap">
        <button
          type="button"
          class="an26-holdings__more"
          aria-controls="${tableId}"
          aria-label="Show ${hiddenRowsCount} more holdings rows"
        >
          Show More
        </button>
      </div>`
          : ""
      }
      ${disclaimer ? `<p class="an26-holdings__disc">${formatRichTextHtml(disclaimer)}</p>` : ""}
    </div>`;

  const tbody = container.querySelector(".js-an26-holdings-body");
  const theadRow = container.querySelector<HTMLTableRowElement>(".an26-holdings__table thead tr");
  const moreWrap = container.querySelector<HTMLElement>(".js-an26-holdings-more-wrap");
  const moreBtn = container.querySelector<HTMLButtonElement>(".an26-holdings__more");

  const paintBody = () => {
    if (!tbody) return;
    tbody.innerHTML = displayedRows().map(rowHtml).join("");
  };

  const paintMoreControl = () => {
    if (!moreWrap || !moreBtn) return;
    const shouldHide = hiddenRowsCount === 0;
    moreWrap.hidden = shouldHide;
    moreWrap.setAttribute("aria-hidden", shouldHide ? "true" : "false");
    moreWrap.style.display = shouldHide ? "none" : "flex";
    if (shouldHide) return;
    if (expanded) {
      moreBtn.textContent = "Show Less";
      moreBtn.setAttribute("aria-label", "Show fewer holdings rows");
    } else {
      moreBtn.textContent = "Show More";
      moreBtn.setAttribute(
        "aria-label",
        `Show ${hiddenRowsCount} more holdings rows`,
      );
    }
  };

  const paintSortAria = () => {
    const ths = container.querySelectorAll<HTMLTableCellElement>(".an26-holdings__th[data-sort]");
    ths.forEach((th) => {
      const k = th.dataset.sort as keyof HoldingsRow;
      if (sortKey === k) th.setAttribute("aria-sort", sortDir === "asc" ? "ascending" : "descending");
      else th.setAttribute("aria-sort", "none");
    });
  };

  const setSort = (key: keyof HoldingsRow) => {
    if (sortKey === key) sortDir = sortDir === "asc" ? "desc" : "asc";
    else {
      sortKey = key;
      sortDir = "asc";
    }
    paintBody();
    paintSortAria();
    paintMoreControl();
  };

  const onHeaderPointer = (e: Event) => {
    const th = (e.target as HTMLElement).closest<HTMLTableCellElement>("th[data-sort]");
    if (!th?.dataset.sort) return;
    setSort(th.dataset.sort as keyof HoldingsRow);
  };

  const onHeaderKey = (e: Event) => {
    if (!(e instanceof KeyboardEvent)) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    const th = (e.target as HTMLElement).closest<HTMLTableCellElement>("th[data-sort]");
    if (!th?.dataset.sort) return;
    e.preventDefault();
    setSort(th.dataset.sort as keyof HoldingsRow);
  };

  paintBody();
  paintSortAria();
  paintMoreControl();

  theadRow?.addEventListener("click", onHeaderPointer);
  theadRow?.addEventListener("keydown", onHeaderKey);

  const onToggleExpand = () => {
    expanded = !expanded;
    paintBody();
    paintMoreControl();
    if (expanded) {
      const firstRevealedCell = tbody?.querySelector<HTMLTableCellElement>(
        `tr:nth-child(${PREVIEW_ROW_COUNT + 1}) td`,
      );
      firstRevealedCell?.setAttribute("tabindex", "-1");
      firstRevealedCell?.focus();
    } else {
      moreBtn?.focus();
    }
  };

  const onCsv = () => {
    const header = SORT_KEYS.map((k) => TH_LABELS[k]);
    const lines = [header.join(",")].concat(
      sortedRows(base).map((r) =>
        [
          r.ticker,
          r.cusip,
          `"${r.name.replace(/"/g, '""')}"`,
          `"${r.sector.replace(/"/g, '""')}"`,
          String(r.weight),
          holdingsMarketCapLabel(r.marketCap),
        ].join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "holdings.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPdf = () => {
    if (pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${title || "holdings"}.pdf`;
      a.click();
      return;
    }
    window.print();
  };

  const csvBtn = container.querySelector(".js-an26-csv");
  const pdfBtn = container.querySelector(".js-an26-pdf");
  csvBtn?.addEventListener("click", onCsv);
  pdfBtn?.addEventListener("click", onPdf);
  moreBtn?.addEventListener("click", onToggleExpand);

  return () => {
    theadRow?.removeEventListener("click", onHeaderPointer);
    theadRow?.removeEventListener("keydown", onHeaderKey);
    csvBtn?.removeEventListener("click", onCsv);
    pdfBtn?.removeEventListener("click", onPdf);
    moreBtn?.removeEventListener("click", onToggleExpand);
    container.replaceChildren();
  };
}

function chartlessId(title: string | null): string {
  return String(title || "holdings")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
